import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useEagleEyeNetworkStore, useSyncTabsEffect } from "state/zustand-stores/eagle-eye-network-store";
import { useAuthenticateEenMutation } from "graphql/mutations/authenticate-een.generated";

/**
 * Gets the current application base URL based on the environment
 */
function getBaseUrl(): string {
	if (typeof window === "undefined") return "";

	const { protocol, hostname, port } = window.location;
	const portString = port ? `:${port}` : "";

	return `${protocol}//${hostname}${portString}`;
}

/**
 * Custom hook to handle Eagle Eye Network OAuth authentication flow
 */
export function useEagleEyeAuth() {
	const [params, setParams] = useSearchParams();
	const code = params.get("code");
	const logout = params.get("logout");

	const { isAuthenticated, setIsAuthenticated, clearAuthData } = useEagleEyeNetworkStore();
	const [authenticateEen, { loading, error }] = useAuthenticateEenMutation();

	// Tracks the last code we started exchanging so React StrictMode's double-effect
	// invocation (and any other re-runs) never send the same single-use code twice.
	const exchangedCodeRef = useRef<string | null>(null);

	// Sync auth state across browser tabs
	useSyncTabsEffect();

	// Handle logout parameter
	useEffect(() => {
		if (logout) {
			params.delete("logout");
			setParams(params);
			clearAuthData();
		}
	}, [logout, clearAuthData, params, setParams]);

	// Handle OAuth callback with authorization code.
	// The ref guard prevents the same single-use code from being sent twice —
	// this matters both for React StrictMode (which double-invokes effects in dev)
	// and for the loginInPopup flow where multiple windows mount this hook.
	useEffect(() => {
		if (code && !isAuthenticated && exchangedCodeRef.current !== code) {
			exchangedCodeRef.current = code;
			const redirectUri = `${getBaseUrl()}/eagle-eye`;

			authenticateEen({
				variables: {
					input: {
						code,
						redirectUri
					}
				}
			})
				.then(({ data }) => {
					if (data?.authenticate_een?.success) {
						setIsAuthenticated(true);
						params.delete("code");
						setParams(params);
					} else {
						// Exchange returned but wasn't successful — allow a retry
						exchangedCodeRef.current = null;
					}
				})
				.catch(() => {
					// Allow a manual retry if the exchange fails
					exchangedCodeRef.current = null;
				});
		}
	}, [code]);

	/**
	 * Initiates the OAuth authorization flow
	 * Redirects to Eagle Eye authorization page
	 */
	const buildAuthUrl = (redirectUri: string): string => {
		const url = "https://auth.eagleeyenetworks.com/oauth2/authorize";
		const authParams = {
			response_type: "code",
			scope: "vms.all",
			client_id: process.env.REACT_APP_EAGLE_EYE_CLIENT_ID || "",
			redirect_uri: redirectUri
		};
		return `${url}?${new URLSearchParams(authParams).toString()}`;
	};

	/**
	 * Initiates the OAuth authorization flow
	 * Redirects the current page to Eagle Eye authorization
	 */
	const login = () => {
		const redirectUri = `${getBaseUrl()}/eagle-eye`;
		window.location.replace(buildAuthUrl(redirectUri));
	};

	/**
	 * Initiates the OAuth authorization flow in a popup window.
	 * The popup redirects back to /eagle-eye, processes the code, stores tokens
	 * in the shared Zustand store (persisted to localStorage), then closes itself.
	 * The calling window receives the updated auth state via useSyncTabsEffect.
	 */
	const loginInPopup = () => {
		const redirectUri = `${getBaseUrl()}/eagle-eye`;
		window.open(buildAuthUrl(redirectUri), "eagle-eye-auth", "width=600,height=700,scrollbars=yes,resizable=yes");
	};

	/**
	 * Logs out by clearing auth data
	 */
	const handleLogout = () => {
		clearAuthData();
	};

	return {
		// State
		isAuthenticated,
		isAuthenticating: loading,
		authError: error?.message,

		// Actions
		login,
		loginInPopup,
		logout: handleLogout
	};
}
